<template>
    <div v-if="enableRequired" class="float-end d-flex align-items-end gap-2">
        <div class="thetalake-tenant text-start">
            <label class="form-label small mb-1" :for="tenantInputId">Theta Lake Portal URL</label>
            <div class="input-group input-group-sm">
                <input :id="tenantInputId"
                       v-model="tenantInput"
                       type="text"
                       class="form-control"
                       placeholder="preview1"
                       autocomplete="off"
                       spellcheck="false"
                       :aria-describedby="tenantSuffixId"
                       @keyup.enter="connectDisconnect">
                <span class="input-group-text" :id="tenantSuffixId">.{{ thetalakeDomain }}</span>
            </div>
        </div>
        <button type="button" @click.stop.prevent="connectDisconnect"
                class="btn btn-sm btn-success" :disabled="enableBlocked" :title="buttonTitle">
            {{ label }}
        </button>
    </div>
    <button v-else type="button" @click.stop.prevent="connectDisconnect" :class="{
                'float-end': true, btn:true, 'btn-sm': true,
                'btn-success': connected === false,
                'btn-danger': connected === true
            }">
        {{ label }}
    </button>
</template>

<style scoped>
    /* Wide enough that a realistic tenant name is readable next to the suffix; the
       float-end wrapper would otherwise shrink the input to its placeholder. */
    .thetalake-tenant {
        min-width: 14rem;
    }
</style>

<script>
    import {mapGetters} from 'vuex'
    import {hasParameterToken} from '@/services/ThetalakeParameterToken'

    // DEV-2843: enablement happens in the *customer's own* Theta Lake tenant, which is always
    // `<tenant>.thetalake.ai`. Only the tenant label varies, so we ask for that and hold the
    // domain constant — a full URL field would invite pointing at the wrong host entirely
    // (notably developer-preview.thetalake.ai, our developer portal, where a customer has no
    // account at all).
    const THETALAKE_DOMAIN = 'thetalake.ai';

    // The integrations LIST page, not the `/conferencing` create page. Per Theta Lake (2026-08-18)
    // the install link that actually starts the flow is revealed by hovering the left information
    // icon on this list — it is not a control on the create form, which is why walking the create
    // screens never surfaced it. Sending the user here puts them on the page holding that link.
    const THETALAKE_PORTAL_PATH = '/#/policy-management/integrations';

    /**
     * Reduce whatever the user typed to a bare tenant label.
     *
     * Accepts `preview1`, `preview1.thetalake.ai`, or a full pasted portal URL, because all
     * three are things someone reasonably types into a field whose label says "URL". Anything
     * that is not a valid DNS label character is dropped rather than escaped, so a malformed
     * entry yields an empty tenant and the button stays disabled instead of building a URL to
     * a host that does not exist.
     */
    function tenantFromInput(raw) {
        return String(raw || '')
            .trim()
            .toLowerCase()
            .replace(/^[a-z]+:\/\//, '')          // scheme
            .split('/')[0]                         // path / fragment
            .split(':')[0]                         // port
            .replace(new RegExp(`\\.?${THETALAKE_DOMAIN.replace('.', '\\.')}$`), '')
            .replace(/[^a-z0-9-]/g, '')            // anything not a DNS label char
            .replace(/^-+|-+$/g, '');              // leading/trailing hyphens
    }

    // The template has two mutually exclusive roots on purpose. Every other integration keeps
    // the bare <button> as this component's root element, so class passthrough (PowerupCard
    // passes `full-width`), IntegrationItem's `$el.classList.add('float-end')`, and the
    // `.integration-list>li>.float-end` mobile rule all behave exactly as before. Only the
    // Theta Lake enable state introduces a wrapper.
    //
    // Keep the template free of root-level comments: a comment node counts toward the root
    // count, which turns the v-if/v-else chain into a fragment and silently disables Vue 3's
    // automatic attribute inheritance.

    // Distinguishes instances for label/input `for` association. A module-level counter rather
    // than Vue's internal `_uid`, which is a Vue 2 field this app (Vue 3 via @vue/compat) does
    // not expose.
    let instanceSeq = 0;

    export default {
        props: ['service', 'team', 'beforeAuth'],
        data() {
            return {
                // Prefilled from env so QA does not retype it on every run. Normalized through the
                // same path as user input, so a full URL in either var still reduces to a tenant.
                // The _PORTAL_URL fallback is transitional: it held a full URL before the field
                // existed, and someone may still have it in an untracked .env.local.
                tenantInput: tenantFromInput(
                    process.env.VUE_APP_THETALAKE_TENANT || process.env.VUE_APP_THETALAKE_PORTAL_URL
                ),
                uid: ++instanceSeq
            }
        },
        computed: {
            connected() { return !!this.service && !!this.service.isConnected(this.team) },
            ...mapGetters({
                vendor: 'vendor',
                afterOauthData: 'afterOauthData'
            }),
            /**
             * DEV-2843: Theta Lake uses a server-initiated OAuth flow, so before we can
             * authorize we need a `parameter_token` that only their server can give us.
             * Until we have one the button offers **Enable**, which sends the user to Theta
             * Lake to enable our application; they come back with the token and the button
             * becomes the usual Connect.
             *
             * Deliberately keyed off the service's apiName rather than a new prop, so every
             * existing caller (Mailchimp, HubSpot, Sniply…) keeps its two-state behaviour
             * with no change at the call site.
             */
            enableRequired() {
                return !this.connected
                    && !!this.service
                    && this.service.apiName === 'thetalake'
                    && !hasParameterToken();
            },
            tenant() { return tenantFromInput(this.tenantInput) },
            thetalakeDomain() { return THETALAKE_DOMAIN },
            // Unique per instance: the integrations list renders one of these per account, so a
            // static id would break label/input association for every row after the first.
            tenantInputId() { return `thetalake-tenant-${this.uid}` },
            tenantSuffixId() { return `thetalake-tenant-suffix-${this.uid}` },
            // Only ever blocks the Enable state; Connect and Disconnect are untouched.
            enableBlocked() { return this.enableRequired && !this.tenant },
            enableHint() {
                return 'Theta Lake requires you to enable UpContent from their portal first. '
                    + 'You will be returned here to finish connecting.';
            },
            buttonTitle() {
                if (!this.enableRequired) return null;
                return this.tenant
                    ? `${this.enableHint} Opens ${this.thetalakeEnableUrl()}`
                    : 'Enter your Theta Lake portal URL first.';
            },
            label() {
                if (this.connected) return 'Disconnect';
                return this.enableRequired ? 'Enable' : 'Connect';
            }
        },
        methods: {
            async connectDisconnect() {
                if (this.connected) {
                    this.service.disconnect(this.team);
                } else if (this.enableRequired) {
                    // Guard rather than rely on :disabled alone — the input's Enter handler
                    // reaches this method directly.
                    if (!this.tenant) return;
                    // Send the user to Theta Lake to enable the application. Their portal is
                    // where the integration is created, which is what produces the
                    // parameter_token and the redirect back to our login URL.
                    window.open(this.thetalakeEnableUrl(), '_blank', 'noopener');
                } else {
                    if (this.beforeAuth) await this.beforeAuth();
                    let iframed = this.vendor === 'hootsuite'
                    setTimeout(() => this.service.connect(this.team, { iframed, afterOauthData: this.afterOauthData }), 5);
                }
            },
            thetalakeEnableUrl() {
                // The tenant now comes from the user, which resolves the first of the two
                // blockers recorded here previously: we had no source for a given customer's
                // tenant URL, so this hardcoded our own preview tenant.
                //
                // Still open with Theta Lake: we have not found the screen that enables a
                // registered third-party application. `Add New Integration` under Policies ->
                // Content Source -> Collaboration Platforms does not list UpContent, and
                // integration settings carry no application association. Until that is
                // answered this deep link lands the user in the right area of the right
                // tenant, but they may still have to find the enable step by hand.
                //
                // See tech spec -> Open Questions.
                return `https://${this.tenant}.${THETALAKE_DOMAIN}${THETALAKE_PORTAL_PATH}`;
            }
        }
    }
</script>
